import React, { useEffect, useState } from 'react'
import CustomBreadcrumb from '../common/CustomBreadcrumb';
import Alert from '../common/Alert';
import { Button, Col, Container, Row } from 'react-bootstrap';
import ResponsiveTable from '../Grid/ResponsiveTable';
import InputBox from '../common/InputBox';
import { convertDateForPicker, convertDateTimetoDateFormat, formatDateTime } from "../../helpers/date-formatter";
import { getReportLogs } from '../../services/reportlog-service';
import DatePicker from '../common/DatePicker';
import Loader from '../common/loader';
import { validateAlphaNumeric } from '../../helpers/validations';

export default function ReportLogMaster() {

  let date = new Date();
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 1000,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [reportLogObj, setReportLogObj] = useState({
    productCode: "",
    serialNumber: "",
    validationDate: { fromDate: convertDateTimetoDateFormat(date.setDate(1)), toDate: convertDateTimetoDateFormat(new Date()) },
    financeCode: "",
    vendorCode: "",
  });

  const handleReset = () => {
    setReportLogObj({
      productCode: "",
      serialNumber: "",
      validationDate: { fromDate: convertDateTimetoDateFormat(date.setDate(1)), toDate: convertDateTimetoDateFormat(new Date()) },
      financeCode: "",
      vendorCode: "",
    });
    setData([]);
    setPagination({
      page: 1,
      limit: 1000,
      total: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false
    });
  }
  
  const handleChange = (e, picker) => {
    const { name, value } = e.target;
    setReportLogObj((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (picker) {
      setReportLogObj((prevState => ({
        ...prevState,
        validationDate: {
          fromDate: picker.startDate.format('YYYY-MM-DD'),
          toDate: picker.endDate.format('YYYY-MM-DD')
        }
      })));
    }
  }

  const [headers] = useState([
    { displayName: "Product Code", columnName: "productCode", dataType: "text", width: 170, maxLength: 50, displayIndication: true, required: true },
    { displayName: "Serial Number", columnName: "serialNumber", dataType: "text", width: 170, maxLength: 50, displayIndication: true, required: true },
    { displayName: "Processing Message", columnName: "processingMessage", dataType: "text", width: 240, maxLength: 50, displayIndication: true, required: true },
    { displayName: "Processed On", columnName: "createdOn", dataType: "date", width: 180, displayIndication: true, formatter: formatDateTime },
    { displayName: "Vendor Code", columnName: "vendorCode", dataType: "text", width: 160, maxLength: 50, displayIndication: true, required: true },
    { displayName: "Finance Code", columnName: "financeCode", dataType: "text", width: 160, maxLength: 50, displayIndication: true, required: true },
  ]);

  useEffect(() => {
    let timer = setTimeout(() => {
      setLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [reload]);

  const fetchPage = async (page, append = false) => {
    const isFirstPage = page === 1;
    if (isFirstPage) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    const payload = {
      ...reportLogObj,
      page,
      limit: 1000
    };
    
    try {
      let result = await getReportLogs(payload);
      
      if (result && result.success) {
        if (append) {
          setData(prevData => [...prevData, ...result.data]);
        } else {
          setData(result.data);
        }
        setPagination(result.pagination);
        
        if (result.data.length === 0 && isFirstPage) {
          Alert("info", "No data found");
        }
      } else {
        Alert("error", "Failed to fetch logs");
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      Alert("error", error.response?.data?.message || "Error fetching logs");
    }
    
    if (isFirstPage) {
      setLoading(false);
    } else {
      setLoadingMore(false);
    }
  }

  const getLogs = async () => {
    // Reset data and fetch first page
    setData([]);
    setPagination({
      page: 1,
      limit: 1000,
      total: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false
    });
    await fetchPage(1, false);
  }

  const loadMoreData = async () => {
    if (pagination.hasNext && !loadingMore) {
      await fetchPage(pagination.page + 1, true);
    }
  }

  // Auto-load all data function (optional)
  const loadAllData = async () => {
    if (pagination.total > 0 && pagination.hasNext) {
      setLoadingMore(true);
      
      try {
        const remainingPages = [];
        for (let page = pagination.page + 1; page <= pagination.totalPages; page++) {
          remainingPages.push(page);
        }
        
        // Load remaining pages in batches to avoid overwhelming the server
        const batchSize = 3; // Load 3 pages at a time
        for (let i = 0; i < remainingPages.length; i += batchSize) {
          const batch = remainingPages.slice(i, i + batchSize);
          const promises = batch.map(page => {
            const payload = {
              ...reportLogObj,
              page,
              limit: 1000
            };
            return getReportLogs(payload);
          });
          
          const results = await Promise.all(promises);
          
          results.forEach((result, index) => {
            if (result && result.success) {
              setData(prevData => [...prevData, ...result.data]);
              if (index === results.length - 1) {
                setPagination(result.pagination);
              }
            }
          });
          
          // Small delay between batches to prevent overwhelming the server
          if (i + batchSize < remainingPages.length) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      } catch (error) {
        console.error('Error loading all data:', error);
        Alert("error", "Error loading additional data");
      }
      
      setLoadingMore(false);
    }
  }

  const commonProps = { onChange: handleChange }

  const initialSettingsProps = {
    maxSpan: { 'day': 31 },
    fromDate: convertDateForPicker(date.setDate(1)),
    toDate: convertDateForPicker(new Date())
  };

  return (
    <>
      {loading && <Loader />}
      <CustomBreadcrumb parentMenu={"Report"} menu={"Validation Log Report"}> </CustomBreadcrumb>
      <Container bsPrefix="main-body drop-down">
        <div className="page-title">Report Validation Log</div>
        <Row>
          <Col lg={3} md={12} className="txt-input">
            <InputBox type="text" label="Product Code" name="productCode" value={reportLogObj.productCode} customValidation={validateAlphaNumeric} {...commonProps} />
          </Col>
          <Col lg={3} md={12} className="txt-input">
            <InputBox type="text" label="Serial Number" name="serialNumber" value={reportLogObj.serialNumber} customValidation={validateAlphaNumeric} {...commonProps} />
          </Col>
          <Col lg={3} md={12} className="txt-input">
            <DatePicker label="Validation Date" name="validationDate" value={reportLogObj.validationDate} {...commonProps} initialSettingsProps={initialSettingsProps} required="true" />
          </Col>
        </Row>
        <Row className='mb-2'>
          <Col lg={3} md={12} className="txt-input">
            <InputBox type="text" label="Finance Code" name="financeCode" value={reportLogObj.financeCode} customValidation={validateAlphaNumeric} {...commonProps} />
          </Col>
          <Col lg={3} md={12} className="txt-input">
            <InputBox type="text" label="Vendor Code" name="vendorCode" value={reportLogObj.vendorCode} customValidation={validateAlphaNumeric} {...commonProps} />
          </Col>

          <Col className="display-action-btn">
            <Button onClick={handleReset} className="btn btn-outline-primary reset-button" variant="outline-primary">Reset</Button>
            <Button onClick={getLogs} className="btn btn-primary btn-color btn-go" disabled={loading}>Go</Button>
          </Col>
        </Row>
        
        {/* Pagination Controls */}
        {data.length > 0 && (
          <Row className="mb-2">
            <Col lg={12}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '10px',
                backgroundColor: '#f8f9fa',
                borderRadius: '5px',
                fontSize: '14px'
              }}>
                <div>
                  <strong>Records:</strong> {data.length.toLocaleString()} of {pagination.total?.toLocaleString()} total
                  {pagination.totalPages > 1 && (
                    <span className="ms-2">
                      (Page {pagination.page} of {pagination.totalPages})
                    </span>
                  )}
                </div>
                <div>
                  {pagination.hasNext && (
                    <>
                      <Button 
                        variant="primary" 
                        size="sm" 
                        onClick={loadMoreData}
                        disabled={loadingMore}
                        className="me-2 btn-color"
                      >
                        {loadingMore ? 'Loading...' : 'Load More'}
                      </Button>
                      <Button 
                        variant="primary" 
                        size="sm" 
                        onClick={loadAllData}
                        disabled={loadingMore}
                        className="btn-color"
                      >
                        {loadingMore ? 'Loading All...' : 'Load All'}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        )}
        
        <ResponsiveTable headers={headers} data={data} icons={[]} DisplayExcelDownload={true} dataType={"Logs"} sessionName={"Maintain Validation Log"} setReload={setReload} reload={reload} pageSize={50}></ResponsiveTable>
      </Container>
    </>
  )
}
